import ErrorFallback from "@/components/ErrorFallback";
import ErrorMessage from "@/components/ErrorMessage";
import Title from "@/components/Title";
import { PASSWORD_SYMBOL_REGEX } from "@/components/activation/Constants";
import {
  CONFIRMATION_CODE_NUMBER_VALIDATORS,
  NEW_PASSWORD_CONFIRM_VALIDATORS,
  NEW_PASSWORD_WITHOUT_CHECKLIST_VALIDATORS,
  Validator,
} from "@/components/auth/ValidatableTextField";
import { BackdropIndicator } from "@/components/common/BackdropIndicator";
import { SCLink } from "@/components/common/SCLink";
import { OrgDashboardLabel } from "@/components/org/OrgDashboardLabel";
import { OrgValidatableTextField } from "@/components/org/OrgValidatableTextField";
import { SCAppBar } from "@/components/sleep_checkup_v1/SCAppBar";
import { PasswordCheckListItem } from "@/pages/examinee/activation/SignUp";
import { facilityTheme } from "@/theme/themeV1";
import {
  callConfirmForgotPasswordAPI,
  callOrgResetPasswordAPI,
} from "@/utils/auth";
import { getAxios, getOrgApiServerUrl } from "@/utils/axios";
import { Box, Button, Stack, ThemeProvider, Typography } from "@mui/material";
import Axios from "axios";
import { FormEvent, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";

const USERNAME_VALIDATORS: Validator[] = [
  (value: string) => (value === "" ? "ユーザー名は必須です" : ""),
];

function OrgForgotPassword() {
  const MIN_WIDTH = 1098;
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [showIndicator, setShowIndicator] = useState(false);
  const [username, setUsername] = useState("");
  const [step, setStep] = useState<"username" | "resetPassword">("username");

  const UsernameForm = () => {
    const [usernameValid, setUsernameValid] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage("");
      const data = new FormData(event.currentTarget);
      const inputUsername = data.get("username")?.toString() ?? "";

      try {
        setShowIndicator(true);
        await callOrgResetPasswordAPI(
          getAxios(getOrgApiServerUrl()),
          inputUsername
        );
        setUsername(inputUsername);
        setStep("resetPassword");
      } catch (e: any) {
        if (Axios.isAxiosError(e)) {
          const res: any = e.response?.data;
          if (res?.username) {
            setErrorMessage(res.username[0]);
          } else if (res?.error_messages && res.error_messages.length > 0) {
            setErrorMessage(res.error_messages[0]);
          } else {
            setErrorMessage(e.message);
          }
        } else {
          setErrorMessage(e.message);
        }
      } finally {
        setShowIndicator(false);
      }
    };

    return (
      <Stack
        component="form"
        onSubmit={handleSubmit}
        spacing="40px"
        alignItems="flex-start"
        sx={{ width: "432px" }}
      >
        <Stack spacing="16px" alignItems="flex-start">
          <Typography
            variant="h3"
            sx={{ fontSize: 24, fontWeight: 400, color: "#282828" }}
          >
            パスワードを忘れた場合
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: 14, fontWeight: 400, color: "#282828" }}
          >
            登録したメールアドレスにパスワード再設定用の確認コードをお送りします。ユーザー名を入力してください。
          </Typography>
        </Stack>
        <OrgValidatableTextField
          validators={USERNAME_VALIDATORS}
          id="username"
          label="ユーザー名"
          name="username"
          autoComplete="username"
          autoFocus
          sx={{ gap: 0 }}
          onChangeErrorMessage={(msg) => setUsernameValid(msg === "")}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!usernameValid}
        >
          送信する
        </Button>
        <SCLink variant="caption" to="/org/signin" sx={{ color: "#3378B3" }}>
          ログイン画面に戻る
        </SCLink>
      </Stack>
    );
  };

  const ResetPasswordForm = () => {
    const [confirmationCodeValid, setConfirmationCodeValid] = useState(false);
    const [newPasswordValid, setNewPasswordValid] = useState(false);
    const [rePasswordValid, setRePasswordValid] = useState(false);
    const [password, setPassword] = useState("");

    const validators = {
      passwordLength: () => {
        if (password.length < 8 || 24 < password.length) {
          return ["NG"];
        }
        return null;
      },
      passwordAlphabetLowerAndUpper: () => {
        if (!(password.match(/[a-z]/) && password.match(/[A-Z]/))) {
          return ["NG"];
        }
        return null;
      },
      passwordDigit: () => {
        if (!password.match(/\d/)) {
          return ["NG"];
        }
        return null;
      },
      passwordSymbol: () => {
        if (!password.match(PASSWORD_SYMBOL_REGEX)) {
          return ["NG"];
        }
        return null;
      },
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage("");
      const data = new FormData(event.currentTarget);
      const verificationCode = data.get("verificationCode")?.toString() ?? "";
      const newPassword = data.get("newPassword")?.toString() ?? "";
      const reEnterPassword = data.get("reEnterPassword")?.toString() ?? "";

      if (newPassword !== reEnterPassword) {
        setErrorMessage("パスワードが一致しません。");
        return;
      }

      try {
        setShowIndicator(true);
        await callConfirmForgotPasswordAPI(
          getAxios(getOrgApiServerUrl()),
          username,
          verificationCode,
          newPassword
        );
        navigate("/org/signin", {
          state: {
            message:
              "パスワードの再設定が完了しました。設定したパスワードでログインしてください。",
          },
        });
      } catch (e: any) {
        if (Axios.isAxiosError(e)) {
          const res: any = e.response?.data;
          if (res?.["cognito-error"]) {
            if (
              res["cognito-error"].name === "CodeMismatchException" ||
              res["cognito-error"].name === "ExpiredCodeException"
            ) {
              setErrorMessage(
                "確認コードが正しくありません。再度お試しください。"
              );
            } else {
              setErrorMessage("パスワードがポリシーを満たしていません。");
            }
          } else if (res?.error_messages && res.error_messages.length > 0) {
            setErrorMessage(res.error_messages[0]);
          } else if (res?.confirmation_code) {
            setErrorMessage(res.confirmation_code[0]);
          } else if (res?.password) {
            setErrorMessage(res.password[0]);
          } else {
            setErrorMessage(e.message);
          }
        } else {
          setErrorMessage(e.message);
        }
      } finally {
        setShowIndicator(false);
      }
    };

    return (
      <Stack
        component="form"
        onSubmit={handleSubmit}
        spacing="40px"
        alignItems="flex-start"
        sx={{ width: "432px" }}
      >
        <Stack spacing="16px" alignItems="flex-start">
          <Typography
            variant="h3"
            sx={{ fontSize: 24, fontWeight: 400, color: "#282828" }}
          >
            パスワードの再設定
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: 14, fontWeight: 400, color: "#282828" }}
          >
            登録したメールアドレスにパスワード再設定用の確認コードをお送りしました。確認コードと新しいパスワードを入力してください。
          </Typography>
        </Stack>
        <Stack spacing="24px" sx={{ width: "100%" }}>
          <OrgValidatableTextField
            validators={CONFIRMATION_CODE_NUMBER_VALIDATORS}
            id="verificationCode"
            label="確認コード（6桁）"
            name="verificationCode"
            autoComplete="one-time-code"
            autoFocus
            sx={{ gap: 0 }}
            onChangeErrorMessage={(msg) => setConfirmationCodeValid(msg === "")}
          />
          <Stack spacing="4px" alignItems="flex-start">
            <OrgValidatableTextField
              validators={NEW_PASSWORD_WITHOUT_CHECKLIST_VALIDATORS}
              name="newPassword"
              label="新しいパスワード"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              onChangeErrorMessage={(msg) =>
                setNewPasswordValid(
                  msg === "" &&
                    !validators.passwordLength() &&
                    !validators.passwordAlphabetLowerAndUpper() &&
                    !validators.passwordDigit() &&
                    !validators.passwordSymbol()
                )
              }
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Stack spacing="4px" sx={{ pl: "13px" }}>
              <PasswordCheckListItem
                text="8文字以上24文字以下の長さにしてください"
                validator={validators.passwordLength}
              />
              <PasswordCheckListItem
                text="大文字と小文字の半角英字を含めてください"
                validator={validators.passwordAlphabetLowerAndUpper}
              />
              <PasswordCheckListItem
                text="半角数字を含めてください"
                validator={validators.passwordDigit}
              />
              <PasswordCheckListItem
                text="記号を含めてください"
                validator={validators.passwordSymbol}
              />
            </Stack>
          </Stack>
          <OrgValidatableTextField
            validators={NEW_PASSWORD_CONFIRM_VALIDATORS}
            name="reEnterPassword"
            label="新しいパスワード（確認）"
            type="password"
            id="reEnterPassword"
            autoComplete="new-password"
            onChangeErrorMessage={(msg) => setRePasswordValid(msg === "")}
          />
        </Stack>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={
            !(confirmationCodeValid && newPasswordValid && rePasswordValid)
          }
        >
          設定する
        </Button>
      </Stack>
    );
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeProvider theme={facilityTheme}>
        <>
          <Title title="SLEEP COMPASS" />
          <meta
            name="description"
            content="睡眠健康測定 SLEEP COMPASSの組織用ダッシュボード"
          />
        </>
        <SCAppBar toolbarItem={<OrgDashboardLabel />} minWidth={MIN_WIDTH} />
        <BackdropIndicator open={showIndicator} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 0",
            position: "absolute",
            width: "100%",
            left: "0px",
            top: "72px",
          }}
        >
          <Stack spacing="40px" sx={{ width: "432px" }}>
            <ErrorMessage
              errorMessage={errorMessage ?? ""}
              sx={{ color: "error.main" }}
            />
            {step === "username" && <UsernameForm />}
            {step === "resetPassword" && <ResetPasswordForm />}
          </Stack>
        </Box>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default OrgForgotPassword;
